import { uuidv4 } from '@firebase/util';
import { isPlatformBrowser } from '@angular/common';
import { ElementRef, Inject, Injectable, PLATFORM_ID, signal } from '@angular/core';
import { DataCollectorService } from 'app/core/services/data_collector_services/data_collector_service.service';
import { environment } from 'environments/environment';
import { Observable, Subject, of } from 'rxjs';
import { map } from 'rxjs/operators';
import AircallPhone from 'aircall-everywhere';
import { LocalStorageService } from 'app/core/services/localstorage.service';
import { ActivatedRoute, Router } from '@angular/router';
import { UtilityService } from 'app/core/services/utility_functions/utility_functions.service';
import { moduleKey } from 'app/core/constants/constants';
import { AuthService } from 'app/core/auth/auth.service';
import { UserService } from 'app/core/user/user.service';


@Injectable({
  providedIn: 'root'
})
export class DialerService {

  dialerList = Object.keys(environment.integratedDialers).map(key => environment.integratedDialers[key]);

  private _integratedDialers = {};

  browserTabId: string;

  callEvents: Subject<any> = new Subject<any>();
  airCallInstance: any;
  isPlatformBrowser: boolean;
  visibilityChange: string;
  isActiveTab: boolean = true;
  isOnCall: boolean = false;
  activeCallTabId: string;
  dialerBroadcastChannel = new BroadcastChannel('zuper-connect-connection');
  isUserOnCallOtherTab = new Subject<boolean>();
  dialerOriginSub$ = new Subject<ElementRef<HTMLButtonElement>>();
  toogleDialerSub$ = new Subject<boolean>();
  overlayHiddenSub$ = new Subject<boolean>();
  callZuperAgent = new Subject<any>();
  isDialerOverlayReady = new Subject<any>();
  isDialerReady = false;
  isUserOnCallOtherTabStatus: boolean | undefined = localStorage.getItem('activeCallOnOtherTab') === 'true';
  isCurrentUserZuperAgent = false
  currentZuperAgentNumber = signal<string | null>(null);

  constructor(
    readonly _dataCollectorService: DataCollectorService, 
    private _localStorageService: LocalStorageService, 
    @Inject(PLATFORM_ID) platformId: string, 
    private _route: Router, 
    private _activatedRoute: ActivatedRoute, 
    private _utilityService: UtilityService,
    private _authService: AuthService,
    private _userService: UserService
  ) {
    this.isPlatformBrowser = isPlatformBrowser(platformId);
    this.listenToTelLinkClicks();
    this.initializeTabId();
    this.handleTabClose();
    this.handleTabFocus();
    this.listenBroadcastChannel();
    this.callZuperAgent.subscribe((callDetails) => {
      if (callDetails) {
        this.callEvents.next({
          eventType: 'dial_number',
          eventDetails: callDetails
        })
      }
    });
    this.isDialerOverlayReady.subscribe((status:boolean)=> this.isDialerReady = status);
    this._userService.logout$.subscribe(() => {
      this._integratedDialers = {};
    });
    this._authService.isZuperAgentEnabled$.subscribe((res: boolean)=>{
      this.isCurrentUserZuperAgent = res;
    })
  }

  fetchDialerIntegrations(): Observable<any> {
    const isZuperVoiceEnabled = this._utilityService.companyPolicy?.zuper_connect?.voice?.is_enabled;
    // Double check whether current zuper agent has voice enabled since current user can be zuper agent if 
    // isMessageEnabled enable alone
    const isZuperConnectModuleAccessible = this._utilityService.hasModuleAccess('CONNECT');
    if (isZuperConnectModuleAccessible && this.isCurrentUserZuperAgent && isZuperVoiceEnabled) {
      this._integratedDialers['zuperconnect'] = true;
      return of(this._integratedDialers);
    }

    return this._fetchIntegratedApps().pipe(
      map((response: any) => {
        response.data
          .filter((integration: any) =>
            this.dialerList.includes(integration.plugin_name)
          )
          .forEach((integration: any) => {
            this._integratedDialers[integration.plugin_name] = integration;
          });
        return this._integratedDialers;
      })
    );
  }

  initAircallIntegration(id: string): void {
    if (this.airCallInstance) {
      return this.airCallInstance;
    }
    this.airCallInstance = new AircallPhone({
      'domToLoadPhone': id,
      'size': 'big'
    });
    this.airCallInstance.on('incoming_call', (call: any) => this.callEvents.next({ eventType: 'incoming_call', eventDetails: call }));
    this.airCallInstance.on('outgoing_call', (call: any) => this.callEvents.next({ eventType: 'outgoing_call', eventDetails: call }));
    this.airCallInstance.on('outgoing_answered', (call: any) => this.callEvents.next({ eventType: 'outgoing_answered', eventDetails: call }));
    this.airCallInstance.on('call_end_ringtone', (call: any) => this.callEvents.next({ eventType: 'call_end_ringtone', eventDetails: call }));
    this.airCallInstance.on('call_ended', (call: any) => this.callEvents.next({ eventType: 'call_ended', eventDetails: call }));
    return this.airCallInstance;
  }

  initializeRingCentralMessageListener(ringCentralIframe: ElementRef<HTMLIFrameElement>): void {
    window.addEventListener('message', (event) => {
      const contentWindow = ringCentralIframe.nativeElement.contentWindow;
      if (ringCentralIframe && event.source === contentWindow && event.origin === contentWindow.origin) {
        const data = event.data;
        if (data) {
          switch (data.type) {
            case 'rc-active-call-notify':
              if (data.call.direction === 'Inbound' && data.call.telephonyStatus === 'Ringing') {
                this.callEvents.next({ eventType: 'incoming_call' });
              } else if (data.call.direction === 'Outbound' && data.call.telephonyStatus === '"Ringing"') {
                this.callEvents.next({ eventType: 'outgoing_call' });
              }
              break;
            case 'rc-call-end-notify':
              this.callEvents.next({ eventType: 'call_ended' });
              break;
          }
        }
      }
    });
  }

  initializeZuperConnectMessageListener(zuperConnectIframe: ElementRef<HTMLIFrameElement>): void {
    window.addEventListener('message', (event) => {
      if (zuperConnectIframe && event.source === zuperConnectIframe.nativeElement.contentWindow) {
        const data = event.data;
        console.log(data); // Ensure data is as expected
        switch (data.event) {
          case 'INCOMING_CALL':
            if (this.isActiveTab) {
              this.callEvents.next({ eventType: 'incoming_call' });
            } else {
              this.isOnCall = true;
              if (this._localStorageService.getLocalStorage('isAnyZuperTabOnFocus') === 'false') {
                this.sendCallNotificationFromLastActiveTab(data.data);
              }
            }
            return;
          case 'INCOMING_CALL_ACCEPTED':
            this.callEvents.next({ eventType: 'incoming_call_accepted' });
            if (this.isActiveTab) {
              this.sendMessageToBroadCastChannel({
                event: 'call_connected',
                tabId: this.browserTabId
              })
            }
            return;
          case 'OUTGOING_CALL':
            this.callEvents.next({ eventType: 'outgoing_call' });
            return;
          case 'OUTGOING_CALL_ACCEPTED':
            this.callEvents.next({ eventType: 'outgoing_call_accepted' });
            if (this.isActiveTab) {
              this.sendMessageToBroadCastChannel({
                event: 'call_connected',
                tabId: this.browserTabId
              })
            }
            return;
          case 'MINIMIZED_ON_CALL':
            this.callEvents.next({ eventType: 'minimize_zuper_connect' });
            return;
          case 'MAXIMIZED_ON_CALL':
            this.callEvents.next({ eventType: 'maximize_zuper_connect' });
            return;
          case 'CALL_END':
            this.callEvents.next({ eventType: 'call_end' });
            this.isOnCall = false;
            if (this.isActiveTab) {
              this.sendMessageToBroadCastChannel({
                event: 'call_end',
                tabId: this.browserTabId
              })
            }
            return;
          case 'CALL_REJECTED':
            if (this.isActiveTab) {
              this.callEvents.next({ eventType: 'call_rejected' });
              this.sendMessageToBroadCastChannel({
                event: 'call_end',
                tabId: this.browserTabId
              })
            } else {
              this.isOnCall = false;
            }
            return;
          case 'OPEN_IN_ZUPER':
            this.navigateAppFromZuperConnect(data);
            this.callEvents.next({ eventType: 'open_in_zuper'});
            return;
          case "AGENT_STATUS":
            this.callEvents.next({ eventType: 'agent_status', status: data.data.status });
            return;
          case "THEME":
            this.callEvents.next({ eventType: 'theme', theme: data.data.theme });
            return;
          case 'AUDIO_PERMISSION':
            this.callEvents.next({ eventType: 'audio_permission', state: data.data.state });
            return;
          default:
            console.warn('Unrecognized event:', data.event);
            return;
        }
      }
    });
  }

  navigateAppFromZuperConnect(data: any): void {
    switch (data.data.module) {
      case 'CUSTOMER':
        if(this._utilityService.hasModuleAccess(moduleKey.CUSTOMER)){
          this._route.navigateByUrl('/customers/' + data.data.module_uid + '/details');
        }
        return;
      case 'EMPLOYEE':
        return;
      case 'ADD_CUSTOMER':
        if(this._utilityService.hasModuleAccess(moduleKey.CUSTOMER) && this._utilityService.hasPermission('customer.new')){
          this._route.navigate(['/customers/new'], {
            queryParams: {
              phone_number: data.data.phoneNumber || null
            }
          });
        }
        return;
      case 'ADD_JOB':
        if(this._utilityService.hasModuleAccess(moduleKey.JOB) && this._utilityService.hasPermission('job.new')){
          const customerID = data.data['customer_uid'];
          this._route.navigateByUrl('/jobs/new', {
            state: {
              action: 'clone',
              module: 'CUSTOMER',
              uid: customerID || null
            }
          });
        }
        return;
      case 'CALL_LOG':
      case 'ADD_NOTE':
        this._route.navigate(['/connect/calls/' + (data.data.callUID || data.data.module_uid || '')], {
          queryParams: data.data.module  === 'ADD_NOTE' ? { 'action': "add_note" } : {}
        });
        return;
      case 'MESSAGE':
        this._route.navigate(['/connect/conversations'],{
          queryParams: {
            ...(data.data.phoneNumber ? {number: data.data.phoneNumber}: {}),
            customer_number_uid: data.data.module_uid,
            from_number: data.data.from_number,
            from_number_uid: data.data.from_number_uid,
          }
        });
        return;
    }
  }

  sendMessageToBroadCastChannel(data: any) {
    this.dialerBroadcastChannel.postMessage(data);
    this.isUserOnCallOtherTabStatus = data.event === 'call_connected';
    localStorage.setItem('activeCallOnOtherTab', data.event === 'call_connected' ? 'true' : 'false')
  }

  updateSessionStorageForBrowserVisibility(eventType: 'FOCUS' | 'BLUR') {
    if(eventType === 'FOCUS'){
      sessionStorage.removeItem('minmizedDialerFrameOnBlur');
    } else {
      sessionStorage.setItem('minmizedDialerFrameOnBlur', this.browserTabId);
    }
  }

  private _fetchIntegratedApps(): Observable<any> {
    const url = environment.appStoreURL + 'app/installations';
    const dcRegion: string = localStorage?.getItem('dc_region') || '';
    return this._dataCollectorService.getData(url, {
      region: dcRegion
    });
  }

  private listenToTelLinkClicks(): void {
    if (isPlatformBrowser) {
      // Use native DOM methods to add a listener to all 'a' elements containing 'tel:' in the href.
      document.body.addEventListener('click', (event: Event) => {
        const target = event.target as HTMLElement;
        const parent = target.parentElement as HTMLAnchorElement;
        if (target.tagName.toLowerCase() === 'em' && parent.tagName.toLowerCase() === 'a' && Object.keys(this._integratedDialers).length > 0) {
          if (parent.href.includes('tel:')) {
            event.preventDefault();
            const phoneNo = parent.href.split('tel:')[1];

            if(this._integratedDialers['zuperconnect'] || this._integratedDialers[environment.integratedDialers.ringcentral]) {
              const routeData = this._utilityService.collectRouteData(this._activatedRoute.snapshot, true);
              this.callEvents.next({
                eventType: 'dial_number',
                eventDetails: {
                  'module': routeData.module || null,
                  'module_uid': routeData[`${routeData.module?.toLowerCase()}_uid`] || null,
                  'number': phoneNo,
                  'call_from': this.currentZuperAgentNumber(),
                  'name': null
                }
              });
            } else if (this._integratedDialers['aircall'] && this.airCallInstance) {
              event.preventDefault();
              this.airCallInstance.send(
                'dial_number',
                // eslint-disable-next-line @typescript-eslint/naming-convention
                { phone_number: phoneNo },
                (_: any, data: any) => {
                  this.callEvents.next({
                    eventType: 'dial_number',
                    eventDetails: data
                  });
                }
              );
            }
          }
          // Here you can add custom logic for the clicked tel link
          // prevent the default action if you need
        }
      });
    }
  }

  private sendCallNotificationFromLastActiveTab(data: any): void {
    const isCurrentLastAvailableTab = this.isLastAvailableTab();
    if (isCurrentLastAvailableTab && Notification.permission === 'granted') {
      const title = `Incoming call from ${data.fromName && data.fromName.length > 0 ? data.fromName + ' - ' : ''} ${data.fromNumber}`;
      const n = new Notification(title, {
        icon: 'https://staging.zuperpro.com/assets/images/favicon/apple-touch-icon.png',
        body: `Click to answer!`,
      });
      n.onclick = (): void => {
        window.focus();
        this.callEvents.next({ eventType: 'incoming_call' });
      };
    } else {
      console.log('Not a last active tab or permisison not granted')
    }
  }

  private initializeTabId() {
    this.browserTabId = sessionStorage.getItem('tabId') || uuidv4();
    sessionStorage.setItem('tabId', this.browserTabId);
    this.addCurrentTabId();
  }

  private handleTabClose() {
    window.addEventListener('beforeunload', () => {
      this.removeCurrentTabIdOnClose();
    });
  }

  private handleTabFocus() {
    document.addEventListener("visibilitychange", () => {
      if (document.hidden) {
        this.isActiveTab = false;
        localStorage.setItem('isAnyZuperTabOnFocus', 'false');
      } else {
        this.isActiveTab = true;
        setTimeout(() => {
          this.moveCurrentTabIdToLast();
          localStorage.setItem('isAnyZuperTabOnFocus', 'true');
        }, 0)
      }
    });
  }

  private moveCurrentTabIdToLast() {
    let browserTabIds: string[] = this.getBrowserTabIdList();
    browserTabIds = browserTabIds.filter((id: string) => id !== this.browserTabId);
    browserTabIds.push(this.browserTabId);
    localStorage.setItem('active_zuper_tabs', JSON.stringify(browserTabIds));
  }

  private addCurrentTabId() {
    let browserTabIds: string[] = this.getBrowserTabIdList();
    browserTabIds.push(this.browserTabId);
    localStorage.setItem('active_zuper_tabs', JSON.stringify(browserTabIds));
  }

  private getBrowserTabIdList(): string[] {
    const browserTabIds = localStorage.getItem('active_zuper_tabs');
    return browserTabIds ? JSON.parse(browserTabIds) : [];
  }

  private removeCurrentTabIdOnClose() {
    let browserTabIds: string[] = this.getBrowserTabIdList();
    browserTabIds = browserTabIds.filter((id: string) => id !== this.browserTabId);
    localStorage.setItem('active_zuper_tabs', JSON.stringify(browserTabIds));
  }

  private isLastAvailableTab() {
    let browserTabIds: string[] = this.getBrowserTabIdList();
    return browserTabIds.length > 0 ? browserTabIds[browserTabIds.length - 1] === this.browserTabId : false
  }

  private listenBroadcastChannel() {

    this.dialerBroadcastChannel.onmessage = (ev) => {
      if (ev.data) {
        switch (ev.data.event) {
          case 'call_connected':
            this.activeCallTabId = ev.data.tabId;
            this.isUserOnCallOtherTabStatus = true;
            this.isUserOnCallOtherTab.next(true);
            return;
          case 'call_end':
            this.activeCallTabId = undefined;
            this.isUserOnCallOtherTabStatus = false;
            this.isUserOnCallOtherTab.next(false);
            return;
          default:
            return;
        }
      }
    }
  }

}
